<template>
  <div class="stage-panel-block">
    <section class="stage-panel">
      <p v-if="eyebrow" class="stage-step u-tabular">{{ eyebrow }}</p>
      <h1 class="stage-headline u-display">{{ headline }}</h1>
      <p v-if="intro" class="stage-intro">{{ intro }}</p>
      <!-- Decoration: whatever caption the host put above already carries the
           same fact in text, so a screen reader hears it once rather than
           twice. -->
      <div
        v-if="segments.length"
        class="stage-track"
        data-test="stage-track"
        aria-hidden="true"
      >
        <span
          v-for="segment in segments"
          :key="segment.key"
          class="stage-segment"
          :class="segment.status"
        ></span>
      </div>
    </section>

    <section class="focus-card">
      <slot />
    </section>
  </div>
</template>

<script>
/**
 * The navy stage panel and the focus card that overlaps it.
 *
 * Both pieces were scoped styles inside LessonsView until Daily Play needed the
 * same shape for Today. Copying them would have lost the dark-mode variant
 * (which drops the overlap, because in dark `--al-surface-raised` IS the colour
 * the panel gradient starts from) and the two media-query variants, which is
 * exactly the kind of drift that makes two screens stop looking like one app.
 *
 * The panel carries the one Fraunces display headline the screen is allowed,
 * and the focus card carries the one thing the learner can do right now. The
 * card's contents are a slot rather than props, because the two hosts put
 * different things in it: My Course puts an icon, a title, a description and
 * one of three mutually exclusive actions; Today puts the hero's action and,
 * when there is one, a secondary link to an unfinished older round.
 *
 * Slot content is compiled in the HOST's scope, not this component's, so the
 * shared classes a host writes inside the slot are styled through `:slotted()`
 * below. That is what keeps ONE definition of `.focus-title` rather than one
 * per host.
 */
export default {
  name: "StagePanel",
  props: {
    /** The small uppercase caption above the headline. Optional. */
    eyebrow: { type: String, default: "" },
    /** The one display headline on the screen. */
    headline: { type: String, required: true },
    /** One supporting sentence under the headline. Optional. */
    intro: { type: String, default: "" },
    /**
     * The segmented progress track, as `{ key, status }` entries where status
     * is "done", "current", or anything else for upcoming. An empty list
     * renders no track at all, which is what Today wants: a daily game has no
     * multi-step sequence to show.
     */
    segments: {
      type: Array,
      default: () => [],
      validator: (value) =>
        value.every((entry) => entry && typeof entry.key === "string"),
    },
  },
};
</script>

<style scoped>
/* The block wrapper. It exists for two reasons: it keeps the panel and the
   card as one unit a host can place, and it is the specificity anchor the
   media-query overrides below are written through. Those overrides used to be
   scoped through `.course-setup` in LessonsView so that each one out-ranked its
   base rule by specificity rather than by source order — the production CSS
   minifier reorders equal-specificity duplicate selectors, which has silently
   broken release builds in this repo before (#573). The anchor moved with the
   rules; the reason did not change. */
.stage-panel-block {
  width: 100%;
  min-width: 0;
}

/* The stage panel. Brand navy in BOTH themes, so it uses hue tokens rather than
   role tokens — the split DESIGN.md's 2026-07-04 row records. It is inset, not
   full-bleed: its hosts carry several different padding values across
   breakpoints and clip overflow-x, so a negative-margin bleed would have to
   track all of them, and the host is also the scroll container, so a child
   pulled above its content origin is simply clipped. */
.stage-panel {
  --panel-fg: #f7fafd;
  --panel-fg-muted: rgba(247, 250, 253, 0.78);
  background: linear-gradient(160deg, var(--al-ink-800), var(--al-ink-700));
  border-radius: var(--al-radius-lg);
  box-shadow: var(--al-shadow-sm);
  /* 44px at the bottom so the focus card can overlap by 24px. */
  padding: var(--al-space-xl) var(--al-space-lg) 44px;
  color: var(--panel-fg);
}

.stage-step {
  margin: 0;
  font-size: 12px;
  line-height: 1.35;
  font-weight: 650;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--panel-fg-muted);
}

/* The one display headline on this screen. Weight 700 rather than DESIGN.md's
   750 because public/index.html loads Fraunces at 600/700/800, so 750 is
   synthesised. */
.stage-headline {
  margin: 8px 0 0;
  font-size: 30px;
  line-height: 1.1;
  font-weight: 700;
  color: var(--panel-fg);
}

.stage-intro {
  margin: 10px 0 0;
  font-size: 16px;
  line-height: 1.5;
  color: var(--panel-fg-muted);
  max-width: 320px;
}

/* The wizard's segmented track, same 4px pills. Done is green and the current
   step is warm orange: DESIGN.md line 82 reserves blue for normal lesson
   progress, and these three are completion states rather than lesson progress. */
.stage-track {
  display: flex;
  gap: 6px;
  margin-top: 16px;
}

.stage-segment {
  flex: 1;
  height: 4px;
  border-radius: var(--al-radius-pill);
  background: rgba(247, 250, 253, 0.18);
}

.stage-segment.done {
  background: var(--al-success);
}

.stage-segment.current {
  background: var(--al-orange);
}

/* The focus card: the one thing the learner can do right now. Every property
   is a role token, so it flips cleanly in dark mode. */
.focus-card {
  position: relative;
  margin: -24px 16px 0;
  background: var(--al-surface-raised);
  border: 1px solid var(--al-border);
  border-radius: var(--al-radius-lg);
  box-shadow: var(--al-shadow-md);
  padding: 20px;
}

/* --- The focus card's contents -------------------------------------------
   Written through :slotted() because slot content belongs to the host's style
   scope, not this component's. Keeping them here rather than in each host is
   the whole point of the extraction: two copies of .focus-title would drift. */
:slotted(.focus-header) {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

:slotted(.focus-icon) {
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  border-radius: var(--al-radius-md);
  background: var(--al-warm-soft);
  color: var(--al-ink-900);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

:slotted(.focus-text) {
  min-width: 0;
}

:slotted(.focus-title) {
  margin: 0;
  font-size: 20px;
  line-height: 1.24;
  font-weight: 700;
  color: var(--al-text);
}

:slotted(.focus-description) {
  margin: 4px 0 0;
  font-size: 14px;
  line-height: 1.45;
  color: var(--al-text-muted);
}

:slotted(.focus-action) {
  margin-top: 16px;
}

/* Dark mode drops the overlap instead of inventing a surface hue. In dark
   --al-surface-raised IS --al-ink-800, the colour the panel gradient starts
   from, so an overlapping card would either vanish against the panel or sit
   darker than it, inverting DESIGN.md's rule that lifted surfaces get lighter.
   The card becomes an ordinary raised card below the panel, and the panel's
   bottom padding drops from 44px to 32px because nothing overlaps it any more. */
:root[data-theme="dark"] .stage-panel {
  border: 1px solid var(--al-border);
  padding-bottom: var(--al-space-xl);
}

:root[data-theme="dark"] .focus-card {
  margin-top: 16px;
}

/* --al-warm-soft is a translucent orange in dark, so the chip needs the orange
   glyph rather than the ink one. */
:root[data-theme="dark"] :slotted(.focus-icon) {
  color: var(--al-orange);
}

@media (min-width: 768px) {
  .stage-panel-block .stage-headline {
    font-size: 36px;
  }

  .stage-panel-block .stage-intro {
    max-width: 480px;
  }

  .stage-panel-block .focus-card {
    padding: var(--al-space-lg);
  }
}

/* The narrowest phones (an iPhone SE in landscape-free portrait is 320px). The
   panel loses 4px of side padding. */
@media (max-width: 399px) {
  .stage-panel-block .stage-panel {
    padding-left: 20px;
    padding-right: 20px;
  }
}
</style>
